import { Link, useFetcher } from '@remix-run/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useToast from '~/hooks/useToast';

import RedirectToHiddenInput from '../Forms/RedirectToHiddenInput';
import StatusInputs from '../Forms/StatusInputs';

interface LoginActionProps {
  allowSignup: boolean;
  onSuccess?: () => void;
  hash?: string;
}

const LoginAction = ({ allowSignup = true, onSuccess, hash }: LoginActionProps) => {
  const isLoggedIn = useIsLoggedIn();
  const fetcher = useFetcher<{
    toast: any;
  }>();
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const { t } = useTranslation();

  /**
   * There was a weird bug on a users pub that was not clearing the signup redirects after the final step.
   * This was not happening with other users and was not reproducible locally or on staging. It may have to do with script that was added to the page.
   *
   * Either way, the bug basically forced the final redirect to happen again on the next login attempt. So this is a quick fix to clear the signup redirects
   * before that can happen.
   */
  useEffect(() => {
    localStorage.removeItem('signup_redirects');
  }, []);

  useToast(fetcherData?.toast, onSuccess);

  if (isLoggedIn) {
    return null;
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    let redirectTo = window?.location?.href;

    if (hash) {
      redirectTo = `${redirectTo}#${hash}`;
    }

    formData.append('redirect_to', redirectTo);
    formData.append('request_magic_link', 'true');

    fetcher.submit(formData, { method: 'post', action: '/login' });

    return;
  };

  return (
    <>
      <fetcher.Form method="post" action="/login" className="w-full" onSubmit={(e) => handleFormSubmit(e)}>
        <div className="w-full space-y-2 sm:mx-auto sm:flex sm:space-y-0">
          <StatusInputs
            name="login"
            errorMessage={`${t('errors.default')} ${t('errors.try_later')}`}
            successMessage={t('toast.login_link.message')}
          />
          <div className="w-full">
            <RedirectToHiddenInput />
            <input
              type="email"
              name="email"
              autoComplete="email"
              autoFocus={true}
              placeholder={t('subscribe.email_placeholder') || ''}
              className="block w-full rounded border-gray-300 bg-white p-1.5 text-sm font-light text-black placeholder-gray-400 placeholder-gray-600 placeholder:text-sm focus:outline-none sm:rounded-r-none"
              required
            />
          </div>

          <div>
            <input
              type="submit"
              value={(isSubmitting ? t('login.magic_link_sending') : t('login.send_magic_link')) || ''}
              className="wt-button-font block h-full w-full cursor-pointer rounded border border-transparent bg-wt-primary p-1.5 text-sm font-medium text-wt-text-on-primary transition duration-200 sm:w-fit sm:rounded-l-none"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </fetcher.Form>

      {allowSignup && (
        <div className="mt-2 text-xs text-gray-700">
          <span className="opacity-75">{t('login.sign_up_label')} </span>
          <Link to="/subscribe" className="inline-flex justify-center underline">
            {t('shared.cta.sign_up')}
          </Link>
        </div>
      )}
    </>
  );
};

export default LoginAction;
